import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, ClipboardCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

function StarRow({ label, hint, value, onChange, testId }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-[11px] text-slate-500">{hint}</div>
        </div>
        <span className="font-mono-tech text-xs text-slate-400" data-testid={`${testId}-value`}>{value == null ? "—" : `${value}/5`}</span>
      </div>
      <div className="flex items-center gap-1 mt-2" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            data-testid={`${testId}-${n}`}
            onClick={() => onChange(value === n ? null : n)}
            className="p-0.5 transition-transform hover:scale-110"
            aria-label={`${n} of 5`}
          >
            <Star className={`h-6 w-6 ${value != null && n <= value ? "fill-amber-400 text-amber-400" : "text-slate-700"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SpecialistScoreDialog({ specialist, open, onOpenChange, onSaved }) {
  const [followUp, setFollowUp] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && specialist) {
      setFollowUp(specialist.follow_up_score ?? null);
      setTasks(specialist.task_score ?? null);
      setNote(specialist.performance_note || "");
    }
  }, [open, specialist]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/specialists/${specialist.id}/performance`, {
        follow_up_score: followUp,
        task_score: tasks,
        performance_note: note,
      });
      toast.success("CRM score saved — matching updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!specialist) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="specialist-score-dialog" className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-[#0A8CF2]" /> CRM performance · {specialist.name}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Pull these from your CRM's follow-up and task reports. Higher scores send this specialist more matches.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <StarRow
            label="Follow-up"
            hint="How consistently they follow up on CRM leads"
            value={followUp}
            onChange={setFollowUp}
            testId="score-follow-up"
          />
          <StarRow
            label="Completed tasks"
            hint="CRM tasks closed on time"
            value={tasks}
            onChange={setTasks}
            testId="score-tasks"
          />
          <div>
            <Label className="text-slate-300 mb-1 block">Manager note (optional)</Label>
            <Textarea
              data-testid="score-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 92% follow-up rate in DriveCentric last 30 days"
              className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 min-h-[70px]"
            />
          </div>
          {specialist.performance_updated_at && (
            <p className="text-[11px] text-slate-500" data-testid="score-last-updated">
              Last scored by {specialist.performance_updated_by || "a manager"} on {new Date(specialist.performance_updated_at).toLocaleDateString()}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-score-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save score"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
